import React, { useState } from 'react';
import Box from '@components/Atoms/Box';
import { Typography } from '@mui/material';
import PresentationDiff from '@components/PresentationManagement';
import ShowIf from '@components/Atoms/ShowIf';
import LoadingIndicator from '@components/LoadingIndicator';
import useGetSlideInfo from '../hooks/useGetSlideInfo';
import ToggleButton from '../../Generals/ToggleButton';
import { CompareSlideObject } from '../types';

interface CompareModalContentProps {
  leftSlide: CompareSlideObject | null;
  rightSlide: CompareSlideObject | null;
  onClose: () => void;
}

const CompareModalContent: React.FC<CompareModalContentProps> = ({
  leftSlide,
  rightSlide,
  onClose,
}) => {
  const [displayOption, setDisplayOption] = useState(1);
  const [isSlideModified, setIsSlideModified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { data: leftSlideData, isLoading: isLoadingLeft } = useGetSlideInfo(
    leftSlide?.slideId as string,
  );
  const { data: rightSlideData, isLoading: isLoadingRight } = useGetSlideInfo(
    rightSlide?.slideId as string,
  );

  const getLocalTime = (time?: string) => {
    if (!time) {
      return '';
    }
    const date = new Date(time);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <Box
      sx={{
        overflowy: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '2rem',
        backgroundColor: 'white',
        width: '100%',
        maxHeight: '100%',
        borderRadius: '12px',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'right' }}>
        <Typography variant="h5" sx={{ cursor: 'pointer' }} onClick={() => onClose()}>
          X
        </Typography>
      </Box>
      <ShowIf If={isLoadingLeft || isLoadingRight}>
        <LoadingIndicator content="Fetching slide data..." />
      </ShowIf>
      <ShowIf If={!isLoadingLeft && !isLoadingRight}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-evenly',
            mb: 1,
          }}
        >
          <Box sx={{ flex: 1, px: '4rem' }}>
            <Typography variant="h3" color="black" sx={{ fontWeight: 'bold' }}>
              {leftSlide?.label || 'ChalkTalk Version'}
            </Typography>
            <Typography variant="subtitle1">
              {`Last Updated: ${getLocalTime(leftSlideData?.file.modifiedTime)}`}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, px: '1rem' }}>
            <Typography variant="h3" color="black" sx={{ fontWeight: 'bold' }}>
              {rightSlide?.label || 'My Version (Draft)'}
            </Typography>
            <ShowIf If={isSlideModified}>
              <Typography variant="subtitle1">
                {`Last Updated: ${getLocalTime(rightSlideData?.file.modifiedTime)}`}
              </Typography>
            </ShowIf>
            <ShowIf If={!isSlideModified}>
              <Typography variant="subtitle1">No updates have been made</Typography>
            </ShowIf>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <ToggleButton
            options={['Show All Slides', 'Show Changes Only']}
            selectedOption={displayOption}
            setSelectedOption={(index) => setDisplayOption(index)}
          />
        </Box>
        <ShowIf If={!isLoading && !isSlideModified && displayOption === 1}>
          <Typography variant="h4" sx={{ textAlign: 'center', mt: 2 }}>
            No changes have been made to your draft version.
          </Typography>
        </ShowIf>
        <PresentationDiff
          showAllSlides={displayOption === 0}
          defaultPresentationId={leftSlide?.slideId}
          copyPresentationId={rightSlide?.slideId}
          isReviewer={false}
          setIsSlideModified={setIsSlideModified}
          setIsLoading={setIsLoading}
          learnhubSlideData={[leftSlideData?.slide, rightSlideData?.slide]}
        />
      </ShowIf>
    </Box>
  );
};

export default CompareModalContent;
